/**
 * The `roster` category of the plan resource — a ward's roster, as a plan.
 *
 * A roster used to be draft shift rows carrying a proposal id, published by a
 * batch write. It is a PLAN now: the planning service holds the proposal as the
 * plan's items, `:publish` makes a version the one in force, and care-app's own
 * shift rows are what a published plan produced. Nothing here writes a shift.
 *
 * WHAT THE BOARD READS FROM WHERE. The published roster is `GET /shifts`, which
 * is what the ward is working. The PROPOSAL is this plan's items at its current
 * version. Two reads, because they are two facts: a lead looks at what is
 * planned and at what she is about to publish, and a board that merged them
 * could not tell her which was which.
 */

import type { Plan, PlanItem } from './plans';

/** The `roster` category's name, as the planner declares it. */
export const ROSTER_CATEGORY = 'roster';

/** The subject type a roster plan names: a ward-level location. */
export const ROSTER_SUBJECT_TYPE = 'ward';

/**
 * One proposed shift: the `roster` category's item.
 *
 * `user` is a resource name (`users/{id}`) and `day_label` is the roster DAY,
 * never the window — `planned_start` and `planned_end` are the window, because
 * a night shift labelled the 14th is worked across the following midnight.
 */
export interface RosterPlanItem extends PlanItem {
  id: string;
  user: string;
  day_label: string;
  shift_type_code: string;
  planned_start: string;
  planned_end: string;
  location?: string;
  replaces?: string;
}

/** The period a roster plan covers, and the team it staffs. */
export interface RosterPlanAttributes extends Record<string, unknown> {
  /** `teams/{id}` — the team every published row is written against. */
  team: string;
  period: {
    start_time: string;
    end_time: string;
    time_zone: string;
    day_labels: string[];
  };
}

/** A plan of the `roster` category, with its items and attributes narrowed. */
export interface RosterPlan extends Plan {
  attributes: RosterPlanAttributes;
  items: RosterPlanItem[];
}
